import { PERMISSIONS } from './permissions.js';
export const QUESTIONNAIRE_TEMPLATE_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
export const QUESTIONNAIRE_APPLICATION_SCOPES = [
    'EXISTING_CUSTOMER',
    'NEW_CUSTOMER',
    'PROSPECT',
    'SUPPLIER',
    'PARTNER',
    'PARTNERLESS',
    'OTHER',
];
export const QUESTIONNAIRE_QUESTION_TYPES = [
    'SHORT_TEXT',
    'LONG_TEXT',
    'NUMBER',
    'DECIMAL',
    'CURRENCY',
    'PERCENT',
    'DATE',
    'TIME',
    'DATE_TIME',
    'YES_NO',
    'CHECKBOX',
    'SINGLE_CHOICE',
    'MULTIPLE_CHOICE',
    'DROPDOWN',
    'COMBOBOX',
    'RATING_SCALE',
    'STAR_RATING',
    'SLIDER',
    'EMAIL',
    'PHONE',
    'URL',
    'ADDRESS',
    'HEADING',
    'INFORMATION',
    'DIVIDER',
    'FILE_UPLOAD',
    'PHOTO',
    'IMAGE_GALLERY',
    'SIGNATURE',
    'CONSENT',
    'PRODUCT_SINGLE',
    'PRODUCT_MULTI',
    'PRODUCT_CATALOG',
    'PRODUCT_QUANTITY_LIST',
    'REPEATABLE_GROUP',
];
/**
 * Question types whose answer values may be included in generic aggregate
 * statistics. Product metrics are derived separately from relational product
 * answers and therefore deliberately do not belong to this list.
 */
export const QUESTIONNAIRE_AGGREGATABLE_QUESTION_TYPES = [
    'NUMBER',
    'DECIMAL',
    'CURRENCY',
    'PERCENT',
    'YES_NO',
    'CHECKBOX',
    'SINGLE_CHOICE',
    'MULTIPLE_CHOICE',
    'DROPDOWN',
    'COMBOBOX',
    'RATING_SCALE',
    'STAR_RATING',
    'SLIDER',
];
export const QUESTIONNAIRE_LOGIC_OPERATORS = [
    'EQUALS',
    'NOT_EQUALS',
    'CONTAINS',
    'NOT_CONTAINS',
    'GREATER_THAN',
    'LESS_THAN',
    'EMPTY',
    'NOT_EMPTY',
    'ONE_OF',
    'ALL_OF',
    'BETWEEN',
];
export const QUESTIONNAIRE_LOGIC_ACTIONS = [
    'SHOW',
    'HIDE',
    'SKIP_TO_SECTION',
    'MARK_NOT_APPLICABLE',
];
export const QUESTIONNAIRE_LOGIC_COMBINATORS = ['AND', 'OR'];
export const QUESTIONNAIRE_HIDDEN_ANSWER_POLICIES = ['DELETE', 'MARK_NOT_APPLICABLE'];
export const QUESTIONNAIRE_RUN_STATUSES = [
    'DRAFT',
    'IN_PROGRESS',
    'PAUSED',
    'COMPLETED',
    'ABANDONED',
    'SYNC_PENDING',
    'SYNC_FAILED',
];
export const QUESTIONNAIRE_SYNC_STATUSES = [
    'LOCAL_ONLY',
    'PENDING',
    'SYNCING',
    'SYNCED',
    'FAILED',
];
export const QUESTIONNAIRE_POST_PROCESSING_STATUSES = [
    'NOT_REQUIRED',
    'REQUIRED',
    'IN_PROGRESS',
    'COMPLETED',
    'FAILED',
];
export const QUESTIONNAIRE_SYNC_EVENT_TYPES = [
    'DRAFT_CREATED',
    'ANSWERS_SAVED',
    'ATTACHMENT_QUEUED',
    'ATTACHMENT_UPLOADED',
    'COMPLETION_REQUESTED',
    'COMPLETION_CONFIRMED',
    'LOCAL_DATA_CLEARED',
];
export const QUESTIONNAIRE_SYNC_EVENT_STATUSES = [
    'PENDING',
    'PROCESSING',
    'SUCCEEDED',
    'FAILED',
];
export const QUESTIONNAIRE_COMPLETION_ACTION_TYPES = [
    'CREATE_QUOTE_DRAFT',
    'CREATE_ORDER_DRAFT',
    'CREATE_PROSPECT_NEED',
    'CREATE_PARTNER_NOTE',
    'CREATE_FOLLOW_UP',
    'CREATE_TASK',
    'ADD_CONTACT',
    'UPDATE_PROSPECT_PIPELINE',
    'EXPOSE_DMS_FILES',
    'SAVE_PRODUCT_INTEREST',
    'ADD_PRODUCT_WATCHLIST',
];
export const QUESTIONNAIRE_COMPLETION_ACTION_STATUSES = [
    'PENDING',
    'PROCESSING',
    'SUCCEEDED',
    'FAILED',
];
export const QUESTIONNAIRE_PARTNER_CONVERSION_TARGET_ROLES = ['PROSPECT', 'CUSTOMER'];
export const QUESTIONNAIRE_PARTNER_CONVERSION_STATUSES = [
    'PROCESSING',
    'SUCCEEDED',
    'FAILED',
];
export const QUESTIONNAIRE_PRODUCT_INTEREST_LEVELS = ['NONE', 'LOW', 'MEDIUM', 'HIGH'];
export const QUESTIONNAIRE_ATTACHMENT_PROCESSING_STATUSES = [
    'PENDING',
    'PROCESSING',
    'READY',
    'FAILED',
];
export const QUESTIONNAIRE_ANSWER_APPLICABILITIES = [
    'APPLICABLE',
    'NOT_APPLICABLE',
    'REMOVED_BY_LOGIC',
];
export const QUESTIONNAIRE_SENSITIVE_CLASSIFICATIONS = [
    'NONE',
    'PERSONAL',
    'SPECIAL_CATEGORY',
    'CONFIDENTIAL_BUSINESS',
];
export const QUESTIONNAIRE_IMAGE_SOURCES = ['CAMERA', 'GALLERY', 'BOTH'];
export const QUESTIONNAIRE_ALLOWED_FILE_EXTENSIONS = [
    'pdf',
    'jpg',
    'jpeg',
    'png',
    'webp',
    'docx',
    'xlsx',
    'txt',
    'csv',
];
export const QUESTIONNAIRE_ALLOWED_FILE_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
];
export const QUESTIONNAIRE_ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
export const QUESTIONNAIRE_ALLOWED_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
];
export const QUESTIONNAIRE_MAX_SECTIONS = 50;
export const QUESTIONNAIRE_MAX_QUESTIONS = 300;
export const QUESTIONNAIRE_MAX_QUESTIONS_PER_SECTION = 100;
export const QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION = 200;
export const QUESTIONNAIRE_MAX_LOGIC_RULES = 500;
export const QUESTIONNAIRE_MAX_CONDITIONS_PER_RULE = 20;
export const QUESTIONNAIRE_MAX_ATTACHMENTS_PER_RUN = 100;
export const QUESTIONNAIRE_MAX_FILES_PER_QUESTION = 20;
export const QUESTIONNAIRE_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
export const QUESTIONNAIRE_MAX_TITLE_IMAGE_BYTES = 10 * 1024 * 1024;
export const QUESTIONNAIRE_MAX_TOTAL_UPLOAD_BYTES = 250 * 1024 * 1024;
export const QUESTIONNAIRE_MAX_TEXT_LENGTH = 10_000;
export const QUESTIONNAIRE_MAX_SHORT_TEXT_LENGTH = 500;
export const QUESTIONNAIRE_MAX_PATTERN_LENGTH = 128;
export const QUESTIONNAIRE_MAX_PATTERN_VARIABILITY = 4_096;
export const QUESTIONNAIRE_MAX_REPEATABLE_ROWS = 50;
export const QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS = 2_000;
export const QUESTIONNAIRE_PAGE_SIZE_DEFAULT = 25;
export const QUESTIONNAIRE_PAGE_SIZE_MAX = 100;
export const QUESTIONNAIRE_MAX_SYNCHRONOUS_EXPORT_ROWS = 10_000;
export const QUESTIONNAIRE_MAX_BACKGROUND_EXPORT_ROWS = 100_000;
export const QUESTIONNAIRE_MAX_BACKGROUND_EXPORT_BYTES = 250 * 1024 * 1024;
export const QUESTIONNAIRE_EXPORT_EXPIRY_HOURS = 24;
export const QUESTIONNAIRE_CUSTOMER_SESSION_TTL_MINUTES_DEFAULT = 30;
export const QUESTIONNAIRE_CUSTOMER_IDLE_LOCK_MINUTES_DEFAULT = 5;
export const QUESTIONNAIRE_RETENTION_DAYS_DEFAULT = 730;
export const QUESTIONNAIRE_DRAFT_CLEANUP_DAYS_DEFAULT = 30;
export const QUESTIONNAIRE_PERMISSIONS = {
    USE: PERMISSIONS.QUESTIONNAIRE_USE,
    VIEW_OWN_RUNS: PERMISSIONS.QUESTIONNAIRE_VIEW_OWN_RUNS,
    VIEW_ALL_RUNS: PERMISSIONS.QUESTIONNAIRE_VIEW_ALL_RUNS,
    MANAGE_TEMPLATES: PERMISSIONS.QUESTIONNAIRE_MANAGE_TEMPLATES,
    EXPORT_RESULTS: PERMISSIONS.QUESTIONNAIRE_EXPORT_RESULTS,
    MANAGE_SETTINGS: PERMISSIONS.QUESTIONNAIRE_MANAGE_SETTINGS,
};
//# sourceMappingURL=questionnaires.js.map